import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  RadioGroup,
  Tooltip,
} from "../../../common/utils/mui-imports";
import { MinusCirlce, TickCircle } from "iconsax-react";
import HPRRadioButton from "../../../components/common/HPRRadioButton/HPRRadioButton";
import { useAppDispatch } from "../../../core/hooks";
import { EnumPopupType } from "../../Popups/Popup.enum";
import { openPopup } from "../../Popups/Popup.reducer";
import {
  DropDownDTO,
  QuestionChoiceDTO,
} from "../../../common/types/common.dto.types";
import { CopyDetailsPopupProps } from "./CopyDetailsPopup.types";
import HPRDropDownSingleSelect from "../../common/HPRDropDownSingleSelect/HPRDropDownSingleSelect";

const CopyDetailsPopup: React.FC<CopyDetailsPopupProps> = ({
  open,
  onClose,
  sourceQuestion,
  RowIdList,
  onHandleCopy,
}) => {
  const [radioButtonSelect, setRadioButtonSelect] = useState("");
  const [firstRid, setFirstRid] = useState(0);
  const [secondRid, setSecondRid] = useState(0);
  const [typeSelect, setTypeSelect] = useState(0);
  const [options, setOptions] = useState<DropDownDTO[]>([]);
  const typeOptions = [
    {
      Value: "1",
      Label: "Range",
    },
    { Value: "2", Label: "Only" },
  ] as DropDownDTO[];

  const dispatch = useAppDispatch();

  useEffect(() => {
    // Initialize options if needed
    if (RowIdList && RowIdList.length > 0) {
      setOptions(
        RowIdList.map((code) => ({
          Value: code,
          Label: code,
        }))
      );
    }
  }, [RowIdList]);

  const handleCopy = () => {
    if (radioButtonSelect === "all") {
      onHandleCopy(true, []);
    } else {
      let quidList = [];
      if (typeSelect === 2) {
        const rid = `${firstRid}`;
        quidList.push(rid);
      } else {
        const rid1 = `${firstRid}`;
        const rid2 = `${secondRid}`;

        const index1 = RowIdList.indexOf(rid1);
        const index2 = RowIdList.indexOf(rid2);

        // Ensure both RIDs exist in the list
        if (index1 !== -1 && index2 !== -1) {
          const start = Math.min(index1, index2);
          const end = Math.max(index1, index2);
          quidList = RowIdList.slice(start, end + 1); // inclusive range
        }
      }
      onHandleCopy(false, quidList);
    }
    handleClear();
    onClose();
  };

  const handleCancel = () => {
    handleClear();
    onClose();
  };

  const handleSaveClick = () => {
    dispatch(
      openPopup({
        Open: true,
        BodyText: `Are you sure you want to copy the selected details from Requirement ${sourceQuestion.RowId}?<br/>`,
        HeaderText: "Confirmation",
        PopupType: EnumPopupType.YesNoConfirmation,
        YesBtnClick: handleCopy,
        NoButtonText: "No",
        YesButtonText: "Yes",
      })
    );
  };

  const handleClear = () => {
    setSecondRid(0);
    setFirstRid(0);
    setTypeSelect(0);
    setRadioButtonSelect("");
  };

  const handleRadioButtonSelect = (name: string, value: boolean) => {
    setRadioButtonSelect(name);
    setFirstRid(0);
    setSecondRid(0);
    setTypeSelect(0);
  };

  const handleDropDownChange = (_, value: string, name: string) => {
    if (name === "rid1") {
      setFirstRid(Number(value));
    } else if (name === "option") {
      setTypeSelect(Number(value));
    } else if (name === "rid2") {
      setSecondRid(Number(value));
    }
  };

  const IsInValid = () => {
    if (radioButtonSelect === "") {
      return true;
    } else {
      if (radioButtonSelect === "options" && typeSelect === 0) {
        return true;
      } else {
        if (typeSelect === 1 && (firstRid === 0 || secondRid === 0)) {
          return true;
        } else if (typeSelect === 2 && firstRid === 0) {
          return true;
        }
      }
    }
  };

  const getGapCount = (choices?: QuestionChoiceDTO[]): string => {
    const gapCount = choices?.filter((choice) => choice.IsGap).length || 0;
    return gapCount > 0 ? `${gapCount} Gap(s) Selected` : "-";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "8px",
        },
      }}
    >
      <div className="global-header-wrap rem-trak-head-res">
        <h1 className="page_title pl-20 pt-20">
          Copy Details from Requirement {sourceQuestion.RowId}
        </h1>
      </div>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Below is the requirement selected.
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
            {sourceQuestion.RowId}: {sourceQuestion.Requirement}
          </Typography>

          <TableContainer>
            <Table
              stickyHeader
              sx={{
                minWidth: 650,
                "& .MuiTableCell-root": {
                  padding: "10px 20px",
                },
              }}
              size="small"
            >
              {" "}
              <TableHead>
                <TableRow
                  style={{
                    padding: "20px 5px !important",
                    borderRight: "1px solid #b42b2b !important",
                    borderRadius: "5px",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#D8EDF6",
                      minWidth: 80,
                      borderRight: "1px solid #ddd",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#D8EDF6",
                      minWidth: 80,
                      borderRight: "1px solid #ddd",
                    }}
                  >
                    Choice
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#D8EDF6",
                      minWidth: 80,
                      borderRight: "1px solid #ddd",
                    }}
                  >
                    Gap(s)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#D8EDF6",
                      minWidth: 120,
                      borderRight: "1px solid #ddd",
                    }}
                  >
                    Include N/A
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#D8EDF6",
                      minWidth: 130,
                      borderRight: "1px solid #ddd",
                    }}
                  >
                    Allow Comments
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ height: "45px" }}>
                  <TableCell sx={{ padding: "16px" }}>
                    {sourceQuestion?.QuestionTypeName ?? "-"}
                  </TableCell>
                  <TableCell sx={{ padding: "16px" }}>
                    {sourceQuestion?.QuestionCategoryName ?? "-"}
                  </TableCell>
                  <TableCell sx={{ padding: "16px" }}>
                    {getGapCount(sourceQuestion?.QuestionChoices)}
                  </TableCell>
                  <TableCell sx={{ padding: "16px" }}>
                    {sourceQuestion?.IncludeNotApplicable === true ? (
                      <Tooltip title="Yes">
                        <TickCircle
                          size="20"
                          style={{ marginTop: "4px" }}
                          color="#6b9b37"
                        />
                      </Tooltip>
                    ) : sourceQuestion?.IncludeNotApplicable === false ? (
                      <Tooltip title="No">
                        <MinusCirlce
                          size="20"
                          style={{ marginTop: "4px" }}
                          color="#666"
                        />
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: "16px" }}>
                    {sourceQuestion?.AllowComments === true ? (
                      <Tooltip title="Yes">
                        <TickCircle
                          size="20"
                          style={{ marginTop: "4px" }}
                          color="#6b9b37"
                        />
                      </Tooltip>
                    ) : sourceQuestion?.AllowComments === false ? (
                      <Tooltip title="No">
                        <MinusCirlce
                          size="20"
                          style={{ marginTop: "4px" }}
                          color="#666"
                        />
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body1" sx={{ mt: 2 }}>
            The above properties will be copied to the selected item(s) below.
          </Typography>
        </Box>
        <RadioGroup
          name="QuestionAnswer"
          className="flex--container flex-wrap_wrap "
          value={radioButtonSelect}
        >
          <Box sx={{ mb: 2, marginLeft: "7px" }}>
            <FormControlLabel
              control={
                <HPRRadioButton
                  Name={"all"}
                  Value={"all"}
                  ClassName={""}
                  onRadioButtonChange={handleRadioButtonSelect}
                />
              }
              label="Apply to All Requirements"
            />
          </Box>
          <div className="form-group d-flex">
            <HPRRadioButton
              Name={"options"}
              Value={"options"}
              ClassName={""}
              onRadioButtonChange={handleRadioButtonSelect}
            />
            <div className="d-flex" style={{ gap: "10px", width: "100%" }}>
              <HPRDropDownSingleSelect
                Id="RID"
                Label={typeSelect === 2 ? "ID" : "ID (From)"}
                Name="rid1"
                Select={firstRid}
                Items={options}
                onDropDownChange={handleDropDownChange}
                Disabled={radioButtonSelect !== "options" ? true : false}
              />
              <HPRDropDownSingleSelect
                Id="Option"
                Label="Option"
                Name="option"
                Items={typeOptions}
                Select={typeSelect}
                onDropDownChange={handleDropDownChange}
                Disabled={radioButtonSelect !== "options" ? true : false}
              />
              {typeSelect !== 2 && (
                <HPRDropDownSingleSelect
                  Id="RID"
                  Label="ID (To)"
                  Name="rid2"
                  Items={options}
                  Select={secondRid}
                  onDropDownChange={handleDropDownChange}
                  Disabled={radioButtonSelect !== "options" ? true : false}
                />
              )}
            </div>
          </div>
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          aria-label="Cancel"
          className="border-btn"
          title="Cancel"
          onClick={handleCancel}
          style={{ marginRight: "100px !important" }}
        >
          <span className="body-bold secondary-color">Cancel</span>
        </Button>
        <Button
          variant="contained"
          className="primary-btn-small"
          onClick={handleSaveClick}
          disabled={IsInValid()}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyDetailsPopup;
